import React, {useState, useEffect, useCallback, memo, useLayoutEffect, useRef} from 'react'
import { Box, Typography, Button, Divider, Checkbox, MenuItem, ListItemText, Snackbar, Popover, Tabs, Tab, Stack, CircularProgress, IconButton } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useNavigate, useLocation } from 'react-router-dom';
import TableViewIcon from '@mui/icons-material/TableView';
import { SecondButton } from './Controls';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EventIcon from '@mui/icons-material/Event';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';
import jaLocale from 'date-fns/locale/ja';
import { styled, useTheme } from '@mui/system';
import { DataGrid, GridRow, useGridApiRef } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import { apiURL, userURL } from './App';
import { authJson, authFetch } from './apiClient';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// 共通コンテナスタイル
const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  //padding: '30px',
  backgroundColor: '#fff',
  position: 'relative',
  width: '100%',
  height: '100%'
});

// DataGrid カスタムスタイル
export const ThemeDataGrid = styled(DataGrid)(({ theme }) => ({
  fontSize: '12px',
  backgroundColor: theme.palette.secondary.light,
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.main,
  },
  // ヘッダクリック時のフォーカス枠を消す
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus-visible': {
    outline: 'none',
  },
  '& .MuiDataGrid-menuIconButton, & .MuiDataGrid-menuIcon, & .MuiDataGrid-iconButtonContainer': {
    display: 'none !important',
  },
  '& .MuiDataGrid-filler': { backgroundColor: theme.palette.secondary.light },
  '& .MuiDataGrid-root': { backgroundColor: theme.palette.secondary.light },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
}));

// スケジュールカード（縦線位置がスクロールバー発生時もズレないよう補正）
const ScheduleCard = ({ item }) => {
  const scrollRef = useRef(null);
  // 初期/行変化時スクロールトップ
  useLayoutEffect(()=>{
    const el = scrollRef.current; if(!el) return;
    el.scrollTop = 0;
    requestAnimationFrame(()=> requestAnimationFrame(()=> { if(el) el.scrollTop = 0; }));
  }, [item.id, item.rows.length]);
  // スクロールバー幅を測定し CSS 変数 --sbw に格納 (縦線の%位置から割合分を減算するため)
  useLayoutEffect(()=>{
    const el = scrollRef.current; if(!el) return;
    const update = () => {
      // offsetWidth - clientWidth = 垂直スクロールバー(または gutter) 幅
      const sbw = el.offsetWidth - el.clientWidth; // px
      el.style.setProperty('--sbw', sbw + 'px');
    };
    update();
    const ro = new ResizeObserver(()=> update());
    ro.observe(el);
    window.addEventListener('resize', update);
    return ()=> { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);

  /**
   * スケジュール行の開始/終了時刻から表示用テキストとレンジ判定を生成するヘルパー
   * 仕様:
   *  - start 無し           => '終日'
   *  - 09:00-12:00          => 'AM'
   *  - 13:00-18:00          => 'PM'
   *  - start のみ           => 'HH:MM'
   *  - start & end (その他) => 'HH:MM-HH:MM' （isRange=true）
   * @param {string|undefined|null} start 'HH:MM' もしくは 'HH:MM:SS' 形式想定
   * @param {string|undefined|null} end   'HH:MM' もしくは 'HH:MM:SS' 形式想定
   * @returns {{ text: string, isRange: boolean }}
   */
  const formatScheduleTime = (name, start, end) => {
    let text = '';
    let isRange = false;
    if(!start) {
      text = '終日';
    } else {
      const s = start.slice(0,5);
      const e = end ? end.slice(0,5) : null;
      if(e && s === '09:00' && e === '12:00') {
        text = 'AM';
      } else if(e && s === '13:00' && e === '18:00') {
        text = 'PM';
      } else if(!e) {
        text = s;
      } else {
        text = `${s}-${e}`;
        isRange = true;
      }
    }
    return { text, isRange };
  };

  // レンダリング
  return (
    <Box sx={{ border:'1px solid #ddd', bgcolor:'secondary.light', display:'flex', flexDirection:'column', height:'100%', boxSizing:'border-box', fontSize: '12px' }}>
      <Box sx={{ position:'relative', display:'flex', alignItems:'center', px:1, py:0.75, flexShrink:0 }}>
        {/* 左端: ID */}
        <Typography variant="subtitle1" sx={{ fontWeight:'bold', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize: 'inherit' }}>{item.id}</Typography>
        {/* 中央: 名前 (全体幅に対して中央配置) */}
        <Typography
          variant="subtitle1"
          sx={{
            position:'absolute',
            left:'50%',
            top:'50%',
            transform:'translate(-50%, -50%)',
            fontWeight:'bold',
            maxWidth:'70%', // 長すぎる場合の省略
            minWidth:0,
            textAlign:'center',
            overflow:'hidden',
            textOverflow:'ellipsis',
            whiteSpace:'nowrap',
            pointerEvents:'none', // 中央配置用: 下の要素操作を阻害しない
            fontSize: 'inherit'
          }}
        >{item.name}</Typography>
      </Box>
      <Divider sx={{ flexShrink:0 }} />
      <Box sx={{ position:'relative', flex:1, minHeight:0, overflow:'hidden' }}>
        <Box ref={scrollRef} sx={{
          position:'relative', width:'100%', height:'100%', overflowY:'auto', overflowAnchor:'none',
          // scrollbar-gutter で常に余白を確保し、スクロールバー有無で幅変動させない (対応ブラウザ: Chromium/Firefox, Safari はオーバーレイでズレなし)
          scrollbarGutter: 'stable',
          backgroundImage:'linear-gradient(#e0d7c9,#e0d7c9), linear-gradient(#e0d7c9,#e0d7c9)',
          backgroundRepeat:'no-repeat, no-repeat',
          backgroundSize:'1px 100%, 1px 100%',
          // 位置計算: 理想位置(40%,60%) - (その割合 * スクロールバー幅) 
          backgroundPosition:'calc(40% - (var(--sbw,0px) * 0.40)) 0, calc(60% - (var(--sbw,0px) * 0.60)) 0'
        }}>
          <Box display="grid" gridTemplateColumns="40% 20% 40%" sx={{ position:'relative', width:'100%', alignContent:'start' }}>
            {item.rows.map((row, i)=>(
              <React.Fragment key={i}>
                <Box sx={{ minHeight:24, display:'flex', alignItems:'center', justifyContent:'center', px:0.5, py:0 }}>
                  {(() => {
                    const { text, isRange } = formatScheduleTime(row.event_name, row.event_starttime, row.event_endtime);
                    return (
                      <Typography
                        variant="body2"
                        sx={{
                          minWidth: 0,
                          maxWidth: 120,
                          textAlign: 'center',
                          fontSize: '10px',
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          letterSpacing: 0,
                          p: 0
                        }}
                      >{text}</Typography>
                    );
                  })()}
                </Box>
                <Box sx={{ minHeight:24, display:'flex', alignItems:'center', px:0.5, py:0, justifyContent:'center' }}>
                  <Typography variant="body2" sx={{ width:'100%', textAlign:'center', color: row.event_name === '休み' ? 'red' : undefined, fontSize: '10px', lineHeight: 1.1, p: 0 }}>{row.event_name}</Typography>
                </Box>
                <Box sx={{ minHeight:24, display:'flex', alignItems:'center', px:0.5, py:0 }}>
                  <Typography variant="body2" sx={{ width:'100%', textAlign:'left', fontSize: '10px', lineHeight: 1.1, p: 0 }}>{row.event_contents}</Typography>
                </Box>
              </React.Fragment>
            ))}
            {item.rows.length === 0 && <Box sx={{ gridColumn:'1 / span 3', py:0.5, px:1, fontSize:12, color:'#777' }}>(予定なし)</Box>}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// 予定一覧
const SchedulePage = (props) => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [date, setDate] = useState(null);
  const gridRef = useRef(null);
  // 担当者セル編集制御: シンプル化のため強制オープンは行わない
  // 担当者Popover開閉 (行ID管理) - 選択更新で再レンダーしても開いたまま維持するため親で状態持つ
  const [personMenuOpenRowId, setPersonMenuOpenRowId] = useState(null);
  const [personMenuPosition, setPersonMenuPosition] = useState(null); // { top, left }
  const personMenuItemRefs = React.useRef([]); // roving focus refs
  const skipNextClickRef = React.useRef(false); // spaceでの擬似click抑止
  // 担当者Popover: オリジナル行スナップショット & コミット管理
  const [personMenuOriginalRow, setPersonMenuOriginalRow] = useState(null); // {id, ...oldRow}

  // トースト（showToastを下位useCallbackで参照するため上に配置）
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const showToast = useCallback((msg, severity='success') => {
    setToastMsg(msg); setToastSeverity(severity); setToastOpen(true);
  }, []);
  const [serverExporting, setServerExporting] = useState(false);

  // DataGrid 列定義 (取材予定)
  const columns = [
    { field:'id', headerName:'ID', width:50, hide:true, sortable:false },
    { field:'mark', headerName:'取材', width:40, type:'singleSelect', valueOptions:[1,2,3], sortable:false,
      renderCell:(params) => {
        switch(params.value){
          case 1: return <CircleOutlinedIcon color="success" fontSize="small"/>;
          case 2: return <ChangeHistoryOutlinedIcon color="warning" fontSize="small"/>;
          case 3: return <ClearOutlinedIcon color="error" fontSize="small"/>;
          default: return null;
        }
      }
    },
    {
      field:'interview_starttime',
      headerName:'開始時間',
      width:70,
      sortable:false,
      renderCell:(params)=>{
        const v = params.row && params.row.interview_starttime;
        if(!v) return '';
        return v.toString().slice(0,5);
      }
    },
    { field:'interview_name', headerName:'取材名', width:120, sortable:false },
    { field:'interview_place', headerName:'取材場所', width:140, sortable:false },
    { field:'ucv_person_ids', headerName:'担当者', width:130, sortable:false,
      renderCell:(params)=> <PersonsCell row={params.row} /> },
    { field:'is_onair', headerName:'放送', width:50, sortable:false, filterable:false, align:'center', headerAlign:'center',
      renderCell:(params)=>{
        const checked = params.row.mark === 1 && params.row.is_onair;
        const disabled = params.row.mark !== 1; // mark=1 (〇) の時のみ有効
        return (
          <Checkbox
            size="small"
            checked={checked}
            disabled={disabled}
            onClick={(e)=>e.stopPropagation()}
            onChange={(e)=>{
              e.stopPropagation();
              if(disabled) return; // 念のため防御
              toggleOnair(params.row.id);
            }}
          />
        );
      }
    },
    // 放送日列を一番右に追加
    { field:'program_date', headerName:'放送日', width:130, sortable:false, align:'center', headerAlign:'center',
      renderCell:(params)=> <ProgramDateCell row={params.row} /> },
  ];

  // 放送日セル: 日付+カレンダーアイコン+カレンダーPopover
  const ProgramDateCell = ({ row }) => {
    const [open, setOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [value, setValue] = React.useState(row.program_date ? dayjs(row.program_date) : null);

    // ProgramDateCell の関数内
    if (!window.__suppressRowClickRef) window.__suppressRowClickRef = { current: false };
    const suppressRowClickRef = window.__suppressRowClickRef;

    // 放送予定でない場合は入力不可
    const disabled = !row.is_onair;

    // アイコンクリックでPopover開く
    const handleIconClick = (event) => {
      if (disabled) return; // チェックされていない場合は何もしない
      event.stopPropagation();
      // 現在の program_date を日付オブジェクトに変換してセット（null/invalid なら null）
      const d = row.program_date ? dayjs(row.program_date) : null;
      setValue(d && d.isValid() ? d.toDate() : null);
      // Popoverのアンカーセットとオープン
      setAnchorEl(event.currentTarget);
      setOpen(true);
    };

    // 日付変更時の処理: カレンダーで日付選択 → DB更新 → 取材リスト更新
    const handleDateChange = async (newValue) => {
      // カレンダーで日付選択 → Popover閉じる
      setValue(newValue);
      setOpen(false);
      // 日付を YYYY-MM-DD 形式に整形 (dayjsオブジェクトであればフォーマット、そうでなければ空文字列)
      let formatted = '';
      if (newValue) {
        // dayjs型でなければラップ
        const d = (typeof newValue.format === 'function') ? newValue : dayjs(newValue);
        formatted = d.isValid() ? d.format('YYYY-MM-DD') : '';
      }
      // DB更新と取材リスト更新を行う関数に委譲（成功したら取材リストも更新、失敗したらトースト表示）
      commitProgramDateChanges(row.id, formatted);
    };

    // レンダリング
    return (
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%' }}>
        <Typography variant="body2" fontSize="inherit" sx={{ minWidth:0, flex:1, textAlign:'center', display:'flex', alignItems:'center', height:'100%', color: disabled ? '#aaa' : undefined }}>
          {row.program_date || ''}
        </Typography>
        <IconButton 
          size="small"
          color={disabled ? 'default' : 'primary'}
          disabled={disabled}
          onMouseDown={() => { suppressRowClickRef.current = true; }}
          onClick={handleIconClick} 
          sx={{ ml:0.5, display:'flex', alignItems:'center', height:'100%', opacity: disabled ? 0.4 : 1 }}>
          <EventIcon fontSize="small"/>
        </IconButton>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onMouseDown={() => { suppressRowClickRef.current = true; }}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{ paper: { sx: { p: 1 } } }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
            <Button size="small" onClick={() => { setOpen(false); }}>閉じる</Button>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={jaLocale}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={value}
              onChange={handleDateChange}
              disabled={disabled}
            />
          </LocalizationProvider>
        </Popover>
      </Box>
    );
  };
  
  // 担当者セル: 常時表示のアイコンでPopover開閉
  const PersonsCell = ({ row }) => {
    const ids = Array.isArray(row.ucv_person_ids) ? row.ucv_person_ids : [];
    const names = ids.map(id => users.find(u => u.id === id)?.name).filter(Boolean).join(', ');
    const containerRef = React.useRef(null);
    return (
      <Box ref={containerRef} sx={{display:'flex', alignItems:'center', width:'100%', gap:0.5, minHeight:'100%', position:'relative'}}>
        <Box
          component="span"
          onClick={(e)=>{ e.stopPropagation(); navigate(`details/${row.id}`, { state: { referrer: routerLocation.pathname } }); }}
          sx={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis', flex:1, cursor:'pointer'}}
          title={names}
        >
          {names || ''}
        </Box>
        <IconButton
          size="small"
          onClick={(e)=>{ 
            e.stopPropagation();
            if(personMenuOpenRowId === row.id){
              // 閉じる: 変更があればコミット
              commitPersonMenuChanges();
              setPersonMenuPosition(null);
              setTimeout(()=> setPersonMenuOpenRowId(null), 30);
              return;
            }
            // 他行が開いていたら先にコミット
            if(personMenuOpenRowId && personMenuOpenRowId !== row.id){
              commitPersonMenuChanges();
            }
            // 新規オープン: オリジナルスナップショット保存
            setPersonMenuOriginalRow({ id: row.id, ucv_person_ids: Array.isArray(row.ucv_person_ids)? [...row.ucv_person_ids]: [] });
            const el = containerRef.current || e.currentTarget;
            if(el){
              const rect = el.getBoundingClientRect();
              setPersonMenuPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
            }
            setPersonMenuOpenRowId(row.id);
          }}
          sx={{ p:0.25 }}
          aria-label="担当者編集"
        >
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  // 日付を YYYY-MM-DD に整形
  const formatDateYMD = (d) => {
    if(!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth()+1).padStart(2,'0');
    const da = String(d.getDate()).padStart(2,'0');
    return `${y}-${m}-${da}`;
  };

  // サーバ側テンプレート Excel 出力
  const handleServerExportDaily = async () => {
    if(serverExporting) return;
    if(!date){ showToast('日付が未選択です','error'); return; }
    const ymd = formatDateYMD(date);
    setServerExporting(true);
    try {
      const res = await authFetch(`${apiURL}/export/daily?date=${encodeURIComponent(ymd)}`);
      if(!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'エクスポート失敗');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily_export_${ymd}.xlsx`;
      //a.download = `daily_export_${ymd}.xlsm`;    // マクロで自動調節バージョンのときはこっち
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Excelを出力しました','success');
    } catch(e){
      console.error(e);
      showToast('Excel出力に失敗しました','error');
    } finally {
      setServerExporting(false);
    }
  };

  // 取材リスト内部ソート: mark → is_onair(放送予定を先) → onair_order → interview_starttime
  const sortInterviewRows = useCallback((rs) => {
    return [...rs].sort((a,b)=>{
      // mark (null/undefined は最後)
      const am = (a.mark ?? 9999); const bm = (b.mark ?? 9999);
      if (am !== bm) return am - bm;
      // is_onair: true を先 (false は後)
      const ai = a.is_onair ? 0 : 1; const bi = b.is_onair ? 0 : 1;
      if (ai !== bi) return ai - bi;
      // program_date: null は最後
      const ap = a.program_date ? a.program_date : '9999-99-99';
      const bp = b.program_date ? b.program_date : '9999-99-99';
      if (ap < bp) return -1; if (ap > bp) return 1;
      // onair_order: 未設定は大きい値扱い
      const ao = a.onair_order ?? 9999; const bo = b.onair_order ?? 9999;
      if (ao !== bo) return ao - bo;
      // interview_starttime (null は末尾)
      const at = a.interview_starttime ? a.interview_starttime : '99:99';
      const bt = b.interview_starttime ? b.interview_starttime : '99:99';
      if (at < bt) return -1; if (at > bt) return 1;
      // 最後の安定キー id
      return (a.id || 0) - (b.id || 0);
    });
  }, []);
  
  // 初期表示: ユーザ取得
  useEffect(() => {
    (async () => {
      try {
        const data = await authJson(`${userURL}/fullnames/`);
        setUsers(Array.isArray(data) ? data : []);
      } catch(e){ setUsers([]); }
    })();
  }, []);

  // 初期表示: 保存日付復元
  useEffect(() => {
    const isoStr = sessionStorage.getItem('current_date');
    if (isoStr) {
      setDate(new Date(isoStr));
    } else {
      setDate(new Date());
    }
  }, []);

  // 日付変更時: 取材リストのみ先に取得（スケジュールはユーザ取得後の別effectで）
  useEffect(() => {
    if (date) {
      sessionStorage.setItem('current_date', date.toISOString());
      fetchRows();
    }
  }, [date]);

  // ユーザリスト取得完了後 & 日付変更時にスケジュールリスト取得
  useEffect(() => {
    if (users && users.length > 0) {
      fetchSchedules();
    }
  }, [users, date]);

  // 取材リスト取得
  const fetchRows = async () => {
    if (!date) return;
    let newRows = [];
    const todayStr = date.toLocaleDateString('ja-JP');
    const query_params = new URLSearchParams({ interview_date: todayStr });
    try {
      const data = await authJson(`${apiURL}/interviews?` + query_params);
      newRows = data;
    } catch(e){ console.log(e); }
    setRows(sortInterviewRows(newRows));
  };

  // スケジュールリスト取得
  const fetchSchedules = async () => {
    if (!date) return;
    let newRows = [];
    const todayStr = date.toLocaleDateString('ja-JP');
    const query_params = new URLSearchParams({ event_startdate: todayStr });
    try {
      // API呼び出し
      const data = await authJson(`${apiURL}/schedules?` + query_params);
      console.log(data);
      data.forEach(r => {
        // 担当者ごとに分類
        const item = newRows.find(p => p.id === r.user_id);
        if (!item) {
          // 担当者情報取得
          const user = users.find(u => u.id === r.user_id);
          if (user) {
            // 担当者の新規作成
            newRows.push({ id: r.user_id, name: user.name, rows: [r] });
          }
        }
        else {
          // 既存の担当者に追加
          item.rows.push(r);
        }
      });
    } catch(e){ console.log(e); }
    console.log(newRows);
    // '休み'が含まれる担当者を後ろにソート
    const sortedRows = [...newRows].sort((a, b) => {
      const aHasHoliday = a.rows.some(r => r.event_name === '休み');
      const bHasHoliday = b.rows.some(r => r.event_name === '休み');
      if (aHasHoliday === bHasHoliday) return 0;
      return aHasHoliday ? 1 : -1;
    });
    // ソート後のスケジュールセット
    setSchedules(sortedRows);
  };

  // スケジュール配列変化時に外側グリッドスクロールもリセット（上部空白残留防止）
  useLayoutEffect(()=>{
    if(gridRef.current){ gridRef.current.scrollTop = 0; }
  }, [schedules.map(s=>s.id).join(','), date]);

  // 放送トグル: 放送予定DB更新
  const toggleOnair = (rowId) => {
    // 対象行取得
    const newRow = rows.find(r => r.id === rowId);
    if (!newRow) return;
    const nextIsOnair = !newRow.is_onair;
    const form_params = { id: rowId, is_onair: nextIsOnair, change_account: props.user.username }
    if (!nextIsOnair) {
      form_params.program_date = null; // 放送予定解除時は放送日もリセット
    }
    // API 呼び出し
    const body = new FormData();
    body.append('props', JSON.stringify(form_params));
    authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'ChangeOnAir' })
      .then(async () => {
        if (!nextIsOnair) {
          // 放送予定リスト更新（放送予定リストの放送日もリセット）
          await broadcastRowUpdate({ ...newRow, is_onair: false, program_date: null });
        }
        // 取材リストの放送有無更新
        setRows(prev => prev.map(r =>
          r.id === rowId
            ? { ...r, is_onair: nextIsOnair, program_date: !nextIsOnair ? null : r.program_date }
            : r
        ));
        // 成功トーストは控えめに（必要ならコメント解除）
        // showToast('放送有無が保存されました', 'success');
      })
      .catch(() => {
        // 失敗トーストは表示
        showToast('放送有無の保存に失敗しました', 'error');
      });
  };

  // 担当者トグル: 取材リストへ担当者リスト反映
  const togglePersonId = useCallback((rowId, uid) => {
    // 取材リスト更新
    setRows(prev => prev.map(r => {
      if(r.id !== rowId) return r;
      const list = Array.isArray(r.ucv_person_ids) ? [...r.ucv_person_ids] : [];
      const i = list.indexOf(uid);
      if(i >= 0) list.splice(i,1); else list.push(uid);
      return { ...r, ucv_person_ids: list };
    }));
  }, [setRows]);

  // 取材リスト更新 (担当者変更コミット)
  // 変更があればDB更新し、成功したらrows更新 & 編集モード停止
  // 失敗したら旧行を返す
  async function personRowUpdate(newRow, oldRow) {
    // 変更があればDB更新
    const oldIds = Array.isArray(oldRow?.ucv_person_ids) ? [...oldRow.ucv_person_ids].sort((a,b)=>a-b) : [];
    const newIds = Array.isArray(newRow?.ucv_person_ids) ? [...newRow.ucv_person_ids].sort((a,b)=>a-b) : [];
    const changed = JSON.stringify(oldIds) !== JSON.stringify(newIds);
    if(!changed) return newRow; // 変更なし
    // DB更新
    const body = new FormData();
    body.append('props', JSON.stringify({ id: newRow.id, ucv_person_ids: newRow.ucv_person_ids, change_account: props.user.username }));
    try {
      // API呼び出し
      await authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'ChangeUcvPerson' });
      // 取材リスト更新 (順序維持)
      setRows(prev => prev.map(r => r.id === newRow.id ? { ...r, ...newRow } : r));
      // 成功トーストは控えめに（必要ならコメント解除）
      //showToast('担当者が保存されました', 'success');
      // 新しい行を返す
      return newRow;
    }
    catch(e) {
      // 失敗トースト表示
      showToast('担当者の保存に失敗しました', 'error');
      // 旧行を返す
      return oldRow || newRow;
    }
  }

  // 担当者Popover 閉鎖時コミット
  const commitPersonMenuChanges = useCallback(()=>{
    if(personMenuOpenRowId && personMenuOriginalRow){
      const current = rows.find(r => r.id === personMenuOpenRowId);
      if(current){
        // 変更があればDB更新
        personRowUpdate(current, personMenuOriginalRow);
      }
    }
    setPersonMenuOriginalRow(null);
  }, [personMenuOpenRowId, personMenuOriginalRow, rows]);

  // 担当者Popover 初期フォーカス
  useEffect(()=>{
    if(personMenuOpenRowId && personMenuPosition){
      setTimeout(()=>{
        const first = document.querySelector('#persons-popover [data-person-menu-item]');
        if(first) first.focus();
      },30);
    }
  },[personMenuOpenRowId, personMenuPosition]);

  // 担当者Popover キーハンドリング
  useEffect(()=>{
    const onKey = (e)=>{
      if(!personMenuOpenRowId) return;
      if(e.key === 'Escape' || e.key === 'Enter') {
        // Esc/Enter: 閉じる (変更があればコミット)
        commitPersonMenuChanges();
        setPersonMenuPosition(null);
        setTimeout(()=> setPersonMenuOpenRowId(null), 5);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return ()=> window.removeEventListener('keydown', onKey, true);
  },[personMenuOpenRowId, commitPersonMenuChanges]);

  // 放送予定リスト更新
  const broadcastRowUpdate = async (row) => {
    console.log('放送予定更新開始', row);
    // DB検索API呼び出し用
    let existBroadcast = null;
    // API呼び出し用
    const body = new FormData();
    const form_params = { };
    form_params.program_date = row.program_date;
    form_params.interview_id = row.id;
    form_params.clip_id = null;
    form_params.onair_order = null;
    form_params.entry_account = props.user.username;
    form_params.change_account = props.user.username;
    // 更新処理
    try {
      // 放送予定が存在するか確認のためAPI呼び出し
      const query_params = new URLSearchParams({ interview_id: row.id });
      const data = await authJson(`${apiURL}/broadcasts?` + query_params);
      console.log('放送予定検索結果', data);
      if (data && Array.isArray(data) && data.length > 0) {
        existBroadcast = data[0];
      }
      // 放送予定が存在する場合
      if (existBroadcast) {
        // idを追加する
        form_params.id = existBroadcast.id;
        // 放送予定　更新API呼び出し
        body.append('props', JSON.stringify(form_params));
        await authFetch(`${apiURL}/broadcasts/update`, { method: 'POST', body, actionName: 'AddInterviewToBroadcast' });
      }
      // 放送予定が存在しない場合
      else {
        // 放送予定　新規登録API呼び出し
        body.append('props', JSON.stringify(form_params));
        await authFetch(`${apiURL}/broadcasts/create`, { method: 'POST', body, actionName: 'AddInterviewToBroadcast' });
      }
    } catch(e){ console.log(e); }
  };

  // 取材リスト更新 (取材日変更コミット)
  // 変更があればDB更新し、成功したらrows更新 & 編集モード停止
  // 失敗したら旧行を返す
  async function programDateRowUpdate(newRow, oldRow) {
    // 変更があればDB更新
    const changed = oldRow?.program_date !== newRow?.program_date;
    if(!changed) return newRow; // 変更なし
    // DB更新
    const body = new FormData();
    body.append('props', JSON.stringify({ id: newRow.id, program_date: newRow.program_date, change_account: props.user.username }));
    try {
      // API呼び出し
      await authFetch(`${apiURL}/interviews/update`, { method: 'POST', body, actionName: 'ChangeProgramDate' });
      // 放送予定リスト更新
      await broadcastRowUpdate(newRow);
      // 取材リスト更新 (順序維持)
      setRows(prev => prev.map(r => r.id === newRow.id ? { ...r, ...newRow } : r));
      // 成功トーストは控えめに（必要ならコメント解除）
      //showToast('放送日が保存されました', 'success');
      // 新しい行を返す
      return newRow;
    }
    catch(e) {
      // 失敗トースト表示
      showToast('放送日の保存に失敗しました', 'error');
      // 旧行を返す
      return oldRow || newRow;
    }
  }

  // 放送日Popover 閉鎖時コミット
  const commitProgramDateChanges = useCallback((rowId, date)=> {
    const current = rows.find(r => r.id === rowId);
    if(current){
      // 変更があればDB更新
      programDateRowUpdate({ ...current, program_date: date }, current);
    }
  }, [rows]);

  // 取材グリッドセルクリック処理抽出
  const handleInterviewCellClick = useCallback((params, event) => {
    // 担当者列クリックの場合
    if (params.field === 'ucv_person_ids') {
      // 担当者の▼アイコンクリックかどうか判定
      const isIcon = event.target && event.target.closest && event.target.closest('[data-role="open-person-select"]');
      if (isIcon) {
        // ▼アイコンクリック: 担当者 Popover 開く
        event.stopPropagation();
        if (apiRef.current) {
          const field = 'ucv_person_ids';
            const mode = apiRef.current.getCellMode(params.id, field);
            if (mode === 'view') apiRef.current.startCellEditMode({ id: params.id, field });
        }
        // 詳細画面へ遷移はしない
        return;
      }
    }
    // 放送日列クリックの場合
    else if (params.field === 'program_date') {
      // 放送日セルクリックでPopover開く場合は詳細画面遷移させない（行クリックイベント抑制）
      if (window.__suppressRowClickRef && window.__suppressRowClickRef.current) {
        window.__suppressRowClickRef.current = false;
        // 詳細画面へ遷移はしない
        return;
      }
    }
    // それ以外のセルクリックの場合
    navigate(`details/${params.row.id}`, { state: { referrer: routerLocation.pathname } });
  }, [navigate, routerLocation, apiRef]);

  // レンダリング
  return (
    <Container>
      <Snackbar
        open={toastOpen}
          autoHideDuration={2500}
          onClose={()=> setToastOpen(false)}
          anchorOrigin={{ vertical:'bottom', horizontal:'left' }}
          sx={{ mb: 2, ml: 2 }}
        >
          <MuiAlert elevation={6} variant="filled" severity={toastSeverity} onClose={()=> setToastOpen(false)}>
            {toastMsg}
          </MuiAlert>
        </Snackbar>
        {/* 上部 */}
        <Box sx={{ width: '100%', mt: 2, mb: 0, px: 2, display: 'flex', flexDirection: 'row' }}>
          {/* 左側: 2段レイアウト (1段目: 日付 + 曜日, 2段目: ナビゲーションボタン) */}
          <Box sx={{ width: '50%', minWidth: 400, height: 'auto', px: 2, py: 0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:1 }}>
            {/* 1段目: 日付 */}
            <Box display="flex" alignItems="center" justifyContent="center">
              <Typography variant="inherit" sx={{ fontWeight: 'bold', fontSize: '1.6rem' }}>
                {date ? (()=>{ const w=['日','月','火','水','木','金','土'][date.getDay()]; return `${date.toLocaleDateString('ja-JP')} (${w})`; })() : ''}
              </Typography>
            </Box>
            {/* 2段目: ナビボタンを中央配置 */}
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ width:'100%' }}>
              <Box sx={{ width: '36px' }}></Box>
              <Button
                variant="text"
                sx={{ minWidth: 64, color: '#000' }}
                onClick={() => setDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1))}
                startIcon={<ArrowBackIosIcon sx={{ color: '#000' }} />}
              >前日</Button>
              <Button
                variant="text"
                color="primary"
                sx={{ minWidth: 64 }}
                onClick={() => setDate(new Date())}
              >本日</Button>
              <Button
                variant="text"
                sx={{ minWidth: 64, color: '#000' }}
                onClick={() => setDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1))}
                endIcon={<ArrowForwardIosIcon sx={{ color: '#000' }} />}
              >翌日</Button>
              <IconButton
                color="primary"
                aria-label="カレンダー"
                onClick={(e) => {
                  if (calendarOpen) {
                    setCalendarOpen(false);
                    setCalendarAnchor(null);
                  } else {
                    setCalendarAnchor(e.currentTarget);
                    setCalendarOpen(true);
                  }
                }}
              >
                <EventIcon />
              </IconButton>
            </Stack>
            {/* 3段目: （タブは下部リストエリアへ移動） */}
          </Box>
          {/* 右側: CSVボタン */}
          <Box sx={{ width: '50%', px: 2, py: 0, display:'flex', flexDirection:'column', justifyContent:'flex-end', alignItems:'flex-end' }}>
            {/* フロント側Excel出力ボタンはサーバ移行のため削除 */}
            <SecondButton variant="contained" onClick={handleServerExportDaily} disabled={serverExporting} sx={{ mt:1, position:'relative', minWidth:180 }}>
              <TableViewIcon sx={{ mr:1, opacity: serverExporting?0.3:1 }} />
              {serverExporting ? '出力中...' : 'Excel出力'}
              {serverExporting && (
                <CircularProgress size={20} sx={{ position:'absolute', right:8 }} />
              )}
            </SecondButton>
          </Box>
        </Box>
        {/* 下部: 左リスト (タブ切替) + 右スケジュール */}
        <Box display="flex" flexDirection="row" sx={{ width: '100%', alignItems: 'flex-start', px: 2 }}>
          {/* 左側: 取材 / 放送 切替 (タブをグリッド直上に密着) */}
          <Box sx={{ width: '50%', minWidth: 400, height: '615px', px: 2, py: 0, display:'flex', flexDirection:'column' }}>
            <Tabs
              value="interview"
              textColor="primary"
              indicatorColor="primary"
              sx={{
                minHeight:34,
                mb:0,
                px:0,
                '& .MuiTabs-flexContainer': { mb:0 },
                '& .MuiTab-root': { minHeight:34, py:0, m:0, minWidth:100 },
                borderBottom:'1px solid #ddd'
              }}
            >
              <Tab
                label="取材予定"
                value="interview"
                disableRipple
                sx={{
                  backgroundColor: theme.palette.secondary.light,
                  borderRight: '1px solid #ddd',
                  '&.Mui-selected': { fontWeight: 'bold' }
                }}
              />
            </Tabs>
            <Box sx={{ flex:1, mt:0, display:'flex', minHeight:0, position:'relative' }}>
              <ThemeDataGrid
                rows={rows}
                columns={columns}
                localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                columnVisibilityModel={{ id: false }}
                disableColumnMenu
                disableColumnSorting
                hideFooter
                sx={{ cursor: 'pointer', borderTop:'none', height:'100%' }}
                onCellClick={(params, event) => {
                  handleInterviewCellClick(params, event);
                }}
                processRowUpdate={(newRow, oldRow) => {
                  return personRowUpdate(newRow, oldRow);
                }}
                editMode="cell"
                apiRef={apiRef}
              />
            </Box>
          </Box>
          {/* 右側: 2x3 Grid + List */}
          <Box sx={{ width: '50%', height: '580px', px: 2, py: 0 }}>
            {/* 左タブ分の高さ(約34px)を右パネルにも与えて上端を揃える */}
            <Box sx={{ height: '34px' }} />
            <Box ref={gridRef} display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={1} sx={{ maxHeight: 582, overflow: 'hidden', gridAutoRows: '110px' }}>
              {schedules.map(item => (
                <ScheduleCard key={item.id} item={item} />
              ))}
            </Box>
          </Box>
          {/* 担当者グローバルPopover */}
          <Popover
            id="persons-popover"
            open={!!personMenuOpenRowId && !!personMenuPosition}
            anchorReference="anchorPosition"
            anchorPosition={personMenuPosition || { top:0, left:0 }}
            onClose={()=>{ commitPersonMenuChanges(); setPersonMenuPosition(null); setTimeout(()=> setPersonMenuOpenRowId(null), 30); }}
            transitionDuration={{ appear:150, enter:150, exit:80 }}
            slotProps={{ paper: { sx:{ p:0, minWidth:180 } } }}
          >
            {personMenuOpenRowId && (
              <Box sx={{ width:'100%', maxHeight:230, overflowY:'auto', py:0 }} role="listbox"
                onKeyDown={(e)=>{
                  if(e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); commitPersonMenuChanges(); setPersonMenuPosition(null); setTimeout(()=> setPersonMenuOpenRowId(null), 5); return; }
                  if(e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); commitPersonMenuChanges(); setPersonMenuPosition(null); setTimeout(()=> setPersonMenuOpenRowId(null), 5); return; }
                  if(e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault(); e.stopPropagation();
                    const items = personMenuItemRefs.current.filter(Boolean);
                    if(!items.length) return;
                    const idx = items.indexOf(document.activeElement);
                    let next = 0;
                    if(e.key === 'ArrowDown') next = idx < items.length -1 ? idx+1 : 0; else next = idx > 0 ? idx-1 : items.length-1;
                    items[next].focus();
                    return;
                  }
                }}
              >
                {(() => {
                  const row = rows.find(r => r.id === personMenuOpenRowId);
                  const ids = Array.isArray(row?.ucv_person_ids) ? row.ucv_person_ids : [];
                  personMenuItemRefs.current = [];
                  return users.map(u => {
                    const checked = ids.indexOf(u.id) > -1;
                    return (
                      <MenuItem
                        key={u.id}
                        data-person-menu-item
                        value={u.id}
                        data-value={u.id}
                        selected={checked}
                        tabIndex={0}
                        ref={el => { if(el) personMenuItemRefs.current.push(el); }}
                        onClick={(e)=>{ 
                          // クリック元がチェックボックスなら親で重複トグルしない
                          if(skipNextClickRef.current){
                            skipNextClickRef.current = false; // 一度だけ無効化
                            return;
                          }
                          if(e.target && (e.target.closest && e.target.closest('[data-checkbox]'))) { return; }
                          e.stopPropagation(); togglePersonId(row.id, u.id); 
                        }}
                        onKeyDown={(e)=>{
                          if(e.code === 'Space' || e.key === ' '){
                            e.preventDefault(); e.stopPropagation();
                            skipNextClickRef.current = true; // 後続click抑止
                            togglePersonId(row.id, u.id);
                          }
                        }}
                        sx={{ display:'flex', alignItems:'center', gap:1, py:0.5 }}
                      >
                        <Checkbox
                          size="small"
                          checked={checked}
                          data-checkbox
                          onClick={(e)=>{ e.stopPropagation(); togglePersonId(row.id, u.id); }}
                          onChange={(e)=>{ /* swallow */ }}
                          tabIndex={-1}
                        />
                        <ListItemText
                          primary={u.name}
                          sx={{ flex:1, minWidth:0, overflow:'hidden' }}
                          primaryTypographyProps={{ noWrap: true }}
                        />
                      </MenuItem>
                    );
                  });
                })()}
              </Box>
            )}
          </Popover>
          {/* カレンダー表示 (Popover) */}
          <Popover
            open={calendarOpen}
            anchorEl={calendarAnchor}
            onClose={() => { setCalendarOpen(false); setCalendarAnchor(null); }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            slotProps={{ paper: { sx: { p: 1 } } }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
              <Button size="small" onClick={() => { setCalendarOpen(false); setCalendarAnchor(null); }}>閉じる</Button>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={jaLocale}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={date}
                onChange={newValue => {
                  if (newValue) {
                    setDate(newValue);
                    setCalendarOpen(false);
                    setCalendarAnchor(null);
                  }
                }}
              />
            </LocalizationProvider>
          </Popover>
        </Box>
      </Container>
    );
};

export default SchedulePage;